import { Link } from "react-router-dom";
import { FileText, Lock, Download, ExternalLink, Unlock } from "lucide-react";
import type { DocumentEntry } from "@/api/client";

interface DocumentCardProps {
  document: DocumentEntry;
  ndaRequired: boolean;
  hasAccess: boolean;
}

function typeLabel(type: string): string {
  const map: Record<string, string> = {
    soc2_report: "SOC 2 Report",
    pen_test: "Penetration Test",
    iso_cert: "ISO Certificate",
    policy: "Policy",
    whitepaper: "Whitepaper",
    datasheet: "Datasheet",
    agreement: "Agreement",
    audit_report: "Audit Report",
  };
  return (
    map[type] ??
    type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function typeBadgeColor(type: string): string {
  const map: Record<string, string> = {
    soc2_report: "bg-purple-100 text-purple-700",
    pen_test: "bg-red-100 text-red-700",
    iso_cert: "bg-indigo-100 text-indigo-700",
    policy: "bg-blue-100 text-blue-700",
    whitepaper: "bg-gray-100 text-gray-700",
    audit_report: "bg-amber-100 text-amber-700",
  };
  return map[type] ?? "bg-gray-100 text-gray-700";
}

export default function DocumentCard({
  document,
  ndaRequired,
  hasAccess,
}: DocumentCardProps) {
  const isPublic = document.is_public === 1;
  const requiresNda = document.requires_nda === 1;
  const isLocked = requiresNda && ndaRequired && !hasAccess;
  const canDownload = isPublic || hasAccess || (!requiresNda && !ndaRequired);
  const hasUrl = document.file_url && canDownload;

  return (
    <div className="card flex flex-col p-5 transition-shadow hover:shadow-md">
      <div className="mb-3 flex items-start justify-between">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${
            isLocked ? "bg-gray-50" : "bg-brand-primary/5"
          }`}
        >
          {isLocked ? (
            <Lock className="h-5 w-5 text-gray-400" />
          ) : hasAccess && requiresNda ? (
            <Unlock className="h-5 w-5 text-brand-primary" />
          ) : (
            <FileText className="h-5 w-5 text-brand-primary" />
          )}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${typeBadgeColor(document.document_type)}`}
        >
          {typeLabel(document.document_type)}
        </span>
      </div>

      <h3
        className={`text-sm font-semibold ${isLocked ? "text-gray-400" : "text-gray-900"}`}
      >
        {document.document_name}
      </h3>

      {document.description && (
        <p className="mt-1 flex-1 text-sm text-gray-500 line-clamp-2">
          {document.description}
        </p>
      )}

      <div className="mt-4">
        {isLocked ? (
          <Link
            to="/request"
            className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-opacity hover:opacity-90"
          >
            <Lock className="h-3 w-3" />
            Get access
          </Link>
        ) : hasUrl ? (
          <a
            href={document.file_url ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-primary hover:underline"
          >
            {document.file_url?.startsWith("http") ? (
              <>
                <ExternalLink className="h-3.5 w-3.5" />
                View Document
              </>
            ) : (
              <>
                <Download className="h-3.5 w-3.5" />
                Download
              </>
            )}
          </a>
        ) : (
          <span className="text-xs text-gray-400">No file available</span>
        )}
      </div>
    </div>
  );
}
